import React, { useEffect, useState } from "react";
import {
  useDiscountsTypesQuery,
  usePaymentTypesQuery,
} from "../../Store/Apis/bookingApi";
import { useTaxPercentageQuery } from "../../Store/Apis/toolsApi";

const AddPaymentForm = ({
  handleSubmit,
  totalAmount,
  setPaymentFormModal,
  data,
  defaultEmail = "",
}) => {
  // State for storing the payment data
  const [paymentData, setPaymentData] = useState();

  // Fetching data using custom hooks for Payment and Discount types
  const paymentTypeApi = usePaymentTypesQuery();
  const discountTypeApi = useDiscountsTypesQuery();
  const taxPercentageApi = useTaxPercentageQuery();

  // State to manage form input values
  const [formData, setFormData] = useState({
    discountType: "",
    discount: "",
    paidAmount: "",
    paymentType: "",
    utrNumber: "",
    notes: "",
    specialInstruction: "",
    createQuotation: false,
    sendResendAfterSave: false,
    resendDocumentType: "proforma",
    resendEmail: defaultEmail,
    resendCcEmail: "",
  });

  // Simplified: just use the totalAmount directly as the subtotal
  const subtotal = totalAmount;

  // Effect to populate the form if editing (when `data` is passed as a prop)
  useEffect(() => {
    if (data) {
      // Populate form with existing data for editing
      const formData = {
        discountType: data.discountType,
        discount: data.discount,
        paymentType: data.paymentType,
        utrNumber: data.utrNumber,
        notes: data.notes,
        specialInstruction: data.specialInstruction,
        createQuotation: false,
        sendResendAfterSave: false,
        resendDocumentType: "proforma",
        resendEmail: defaultEmail || "",
        resendCcEmail: "",
      };
      setFormData(formData);
    }
  }, [data, defaultEmail]);

  useEffect(() => {
    if (defaultEmail && !formData.resendEmail) {
      setFormData((prev) => ({
        ...prev,
        resendEmail: defaultEmail,
      }));
    }
  }, [defaultEmail, formData.resendEmail]);

  // Function to handle changes in form inputs
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    // Update formData with the new value
    setFormData({
      ...formData,
      [name]: type === "checkbox" ? checked : value,
    });
  };

  // Function to calculate discount based on the selected Discount Type
  const calculateDiscount = () => {
    const { discountType, discount } = formData;
    if (discountType === "2") {
      // Percentage discount
      return (subtotal * (parseFloat(discount) || 0)) / 100;
    }
    // For flat discount, return the discount amount
    return parseFloat(discount) || 0;
  };

  // Calculate Gross Total after applying the discount
  const grossTotal = subtotal - calculateDiscount();

  // Calculate the balance amount based on Paid Amount
  const balance = !data
    ? grossTotal - (parseFloat(formData.paidAmount) || 0)
    : grossTotal - (parseFloat(formData.paidAmount) || 0) - data.paidAmount;

  // Validation function
  const [errors, setErrors] = useState({});
  const validateForm = () => {
    const newErrors = {};

    // Validate Paid Amount and Payment Type
    if (formData.paidAmount) {
      if (!formData.paymentType || formData.paymentType === "") {
        newErrors.paymentType =
          "Payment Type is required if Paid Amount is entered";
      }
    }

    if (formData.sendResendAfterSave) {
      const emailRegex = /[^@\s]+@[^@\s]+\.[^@\s]+/;
      const emailValue = (formData.resendEmail || defaultEmail || "").trim();

      if (emailValue && !emailRegex.test(emailValue)) {
        newErrors.resendEmail = "Invalid recipient email";
      }

      if (
        formData.resendCcEmail &&
        !emailRegex.test(formData.resendCcEmail.trim())
      ) {
        newErrors.resendCcEmail = "Invalid CC email";
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0; // Form is valid if no errors
  };

  // Handle form submission
  const handleSave = () => {
    if (validateForm()) {
      // If form is valid, proceed to save with simplified amounts
      const paymentFormData = {
        ...formData,
        subtotal: subtotal,
        grossTotal: grossTotal,
        discountAmount: calculateDiscount(),
        balance: balance,
        resendDocument: formData.sendResendAfterSave
          ? {
              documentType: formData.resendDocumentType,
              email: (formData.resendEmail || defaultEmail || "").trim(),
              ccEmail: formData.resendCcEmail.trim(),
            }
          : undefined,
      };

      console.log("Payment form data being submitted:", {
        subtotal: subtotal,
        grossTotal: grossTotal,
        paidAmount: formData.paidAmount,
        discountAmount: calculateDiscount(),
      });

      setPaymentData(paymentFormData);
      handleSubmit(paymentFormData);

      // Reload the page after form submission
      // setTimeout(() => {
      //   window.location.reload();
      // }, 2000); // 2 second delay to allow for API call and toast message
    }
  };

  return (
    <>
      <form noValidate>
        <div className="row">
          {/* Discount Type Dropdown */}
          {/* <div className="col-md-6">
            <div className="mb-3">
              <label className="form-label">Discount Type</label>
              <select
                name="discountType"
                className="form-select"
                value={formData.discountType}
                onChange={handleChange}
                disabled={
                  !discountTypeApi.isSuccess ||
                  discountTypeApi.isFetching ||
                  discountTypeApi.isError
                }
              >
                <option value="">Select Discount Type</option>
                {discountTypeApi?.data?.data.map((elem, i) => (
                  <option value={elem.id} key={i}>
                    {elem.name}
                  </option>
                ))}
              </select>
            </div>
          </div> */}

          {/* Discount Input Field */}
          {/* <div className="col-md-6">
            <div className="mb-3">
              <label className="form-label">Discount</label>
              <input
                type="number"
                name="discount"
                className="form-control"
                placeholder="Enter Amount"
                value={formData.discount}
                onChange={handleChange}
              />
            </div>
          </div> */}
        </div>

        {/* Subtotal, Discount, and Gross Total */}
        <div className="mb-3">
          <span className="form-label d-block ms-0 mb-0">
            <strong>Sub Total:</strong> R {subtotal.toFixed(2)}
          </span>
          <span className="form-label d-block ms-0 mb-0">
            <strong>Discount:</strong>{" "}
            {formData.discountType == 1
              ? `R ${calculateDiscount().toFixed(2)}`
              : formData.discountType == 2
                ? `${formData.discount || 0}% (R ${calculateDiscount().toFixed(
                    2,
                  )})`
                : "R 0.00"}
          </span>
          <span className="form-label d-block ms-0 mb-0">
            <strong>Gross Total:</strong> R {grossTotal.toFixed(2)}
          </span>
        </div>

        <div className="row mb-2">
          {/* Paid Amount Input */}
          <div className="col-md-6">
            <div className="mb-1">
              <label className="form-label">Paid Amount</label>
              <input
                type="number"
                name="paidAmount"
                className="form-control"
                placeholder="Enter Amount"
                value={formData.paidAmount}
                onChange={handleChange}
              />
            </div>

            {/* Display Balance and Amount Paid (if editing) */}
            <div className="mb-3">
              {/* {data && (
                                <label className="form-label"><strong>Amount Paid:</strong> R {data.paidAmount.toFixed(2)}</label>
                            )} */}
              <label className="form-label">
                <strong>Balance:</strong> R {balance.toFixed(2)}
              </label>
            </div>
          </div>

          {/* Payment Type Dropdown */}
          <div className="col-md-6">
            <div>
              <label className="form-label">Payment Type</label>
              <select
                name="paymentType"
                className="form-select"
                value={formData.paymentType}
                onChange={handleChange}
                disabled={
                  !formData.paidAmount ||
                  !paymentTypeApi.isSuccess ||
                  paymentTypeApi.isFetching ||
                  paymentTypeApi.isError
                }
                required={!!formData.paidAmount}
              >
                <option value="">Select Payment Type</option>
                {paymentTypeApi?.data?.data.map((elem, i) => (
                  <option value={elem.id} key={i}>
                    {elem.paymentType}
                  </option>
                ))}
              </select>
              {errors.paymentType && (
                <div className="text-xs text-danger mt-2">
                  {errors.paymentType}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* UTR Number Input */}
        <div className="mb-3">
          <label className="form-label">UTR Number</label>
          <input
            type="text"
            name="utrNumber"
            className="form-control"
            placeholder="Enter Unique Transaction Reference Number"
            value={formData.utrNumber}
            onChange={handleChange}
          />
        </div>

        {/* Notes and Special Instructions */}
        <div className="row">
          <div className="col-md-6">
            <label className="form-label">Notes</label>
            <textarea
              name="notes"
              className="form-control"
              placeholder="Enter Notes"
              value={formData.notes}
              onChange={handleChange}
            ></textarea>
          </div>
          <div className="col-md-6">
            <label className="form-label">Special Instruction</label>
            <textarea
              name="specialInstruction"
              className="form-control"
              placeholder="Enter Special Instructions"
              value={formData.specialInstruction}
              onChange={handleChange}
            ></textarea>
          </div>
        </div>

        {/* Create Quotation Checkbox */}
        {!data && (
          <div className="form-check mt-3">
            <input
              type="checkbox"
              name="createQuotation"
              id="createQuotation"
              className="form-check-input"
              checked={formData.createQuotation}
              onChange={handleChange}
            />
            <label htmlFor="createQuotation" className="form-check-label">
              Do you want to create it as a quotation?
            </label>
          </div>
        )}

        {data && (
          <div className="border rounded p-3 mt-3 bg-light">
            <div className="form-check mb-3">
              <input
                type="checkbox"
                name="sendResendAfterSave"
                id="sendResendAfterSave"
                className="form-check-input"
                checked={formData.sendResendAfterSave}
                onChange={handleChange}
              />
              <label htmlFor="sendResendAfterSave" className="form-check-label">
                Resend the updated document after saving this booking
              </label>
            </div>

            {formData.sendResendAfterSave && (
              <>
                <div className="mb-3">
                  <label className="form-label">Document Type</label>
                  <select
                    name="resendDocumentType"
                    className="form-select"
                    value={formData.resendDocumentType}
                    onChange={handleChange}
                  >
                    <option value="quotation">Quotation</option>
                    <option value="proforma">Proforma Invoice</option>
                    <option value="invoice">Invoice</option>
                  </select>
                </div>

                <div className="mb-3">
                  <label className="form-label">Recipient Email</label>
                  <input
                    type="email"
                    name="resendEmail"
                    className="form-control"
                    placeholder="primary@example.com"
                    value={formData.resendEmail}
                    onChange={handleChange}
                  />
                  {errors.resendEmail && (
                    <div className="text-xs text-danger mt-2">
                      {errors.resendEmail}
                    </div>
                  )}
                </div>

                <div className="mb-3">
                  <label className="form-label">CC Email (Optional)</label>
                  <input
                    type="email"
                    name="resendCcEmail"
                    className="form-control"
                    placeholder="cc@example.com"
                    value={formData.resendCcEmail}
                    onChange={handleChange}
                  />
                  {errors.resendCcEmail && (
                    <div className="text-xs text-danger mt-2">
                      {errors.resendCcEmail}
                    </div>
                  )}
                </div>

                <small className="text-muted">
                  This sends the resend request only after the updated booking
                  is saved successfully.
                </small>
              </>
            )}
          </div>
        )}

        {/* Save and Cancel Buttons */}
        <div className="d-flex justify-content-between mt-5">
          <button
            type="button"
            className="btn btn-secondary"
            onClick={() => setPaymentFormModal(false)}
          >
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-primary"
            onClick={handleSave}
          >
            Save
          </button>
        </div>
      </form>
    </>
  );
};

export default AddPaymentForm;
